/**
 * 求人票の選択状態を管理する型とユーティリティ関数
 *
 * - manual モード: チェックボックスで個別に選択したIDのSet
 * - all モード: 検索結果全件を選択し、除外IDだけを保持する差分方式
 */

export type Selection =
  | { mode: 'manual'; ids: Set<number> }
  | { mode: 'all'; excludedIds: Set<number>; totalCount: number };

/** 初期状態（manual モードの空選択）を生成する */
export const createManualSelection = (): Selection => ({ mode: 'manual', ids: new Set() });

/** 指定IDが選択されているか判定する */
export const isSelected = (s: Selection, id: number): boolean => {
  if (s.mode === 'manual') return s.ids.has(id);
  return !s.excludedIds.has(id);
};

/** 現在の選択件数を返す */
export const selectionCount = (s: Selection): number => {
  if (s.mode === 'manual') return s.ids.size;
  return Math.max(0, s.totalCount - s.excludedIds.size);
};

/** チェック状態のトグル操作を適用した新しい Selection を返す */
export const toggleSelection = (s: Selection, id: number, checked: boolean): Selection => {
  if (s.mode === 'manual') {
    const next = new Set(s.ids);
    if (checked) next.add(id);
    else next.delete(id);
    return { mode: 'manual', ids: next };
  }
  // all モード: チェックを外したら excludedIds に追加、チェックを入れたら除外リストから削除
  const next = new Set(s.excludedIds);
  if (checked) next.delete(id);
  else next.add(id);
  return { mode: 'all', excludedIds: next, totalCount: s.totalCount };
};

/** Selection が all モードかどうかを判定する型ガード */
export const isAllMode = (s: Selection): s is Extract<Selection, { mode: 'all' }> =>
  s.mode === 'all';
